/**
 * Debounce utility for real-time event bridge.
 *
 * Prevents flooding Socket.IO when rapid db.* events fire
 * (e.g., bulk insert triggers many db.table.created in ms).
 * Groups by key and only fires the last event after the debounce window.
 */

type DebouncedCallback = (payload: unknown) => void

interface PendingTimer {
  timer: ReturnType<typeof setTimeout>
  payload: unknown
}

export class RealtimeDebouncer {
  private pending = new Map<string, PendingTimer>()

  /**
   * Debounce a callback by key.
   * If called again with the same key before `ms` elapses,
   * the previous call is cancelled and replaced.
   *
   * @param key - Grouping key (e.g., 'entity:module.entity:id')
   * @param ms - Debounce window in milliseconds
   * @param payload - Event payload
   * @param callback - Function to call after debounce
   * @param onDiscard - Optional callback when a pending event is replaced
   */
  debounce(key: string, ms: number, payload: unknown, callback: DebouncedCallback, onDiscard?: (key: string) => void): void {
    const existing = this.pending.get(key)
    if (existing) {
      clearTimeout(existing.timer)
      onDiscard?.(key)
    }

    const timer = setTimeout(() => {
      this.pending.delete(key)
      callback(payload)
    }, ms)

    this.pending.set(key, { timer, payload })
  }

  /**
   * Cancel all pending debounced calls.
   */
  clear(): void {
    for (const { timer } of this.pending.values()) {
      clearTimeout(timer)
    }
    this.pending.clear()
  }

  /**
   * Number of pending debounced calls.
   */
  get size(): number {
    return this.pending.size
  }
}
